import { createIdentifier } from '@blocksuite/global/di';
import type { BlockStdScope } from '@blocksuite/std';
import {
  type BlockSnapshot,
  type DraftModel,
  type ExtensionType,
  type HistoryStepHold,
  type Slice,
  SNAPSHOT_BLOCK_INSERTER,
  type SnapshotBlockInserter,
  type Store,
  type Text,
  type TransformerMiddleware,
} from '@blocksuite/store';
import { signal } from '@preact/signals-core';
import * as Y from 'yjs';

const META = '__databaseClipboard';
const CUT_LIFETIME_MS = 15 * 60_000;

export interface DatabaseClipboardSource {
  workspaceId: string;
  docId: string;
  databaseId: string;
  blockId: string;
  cutToken?: string;
}

export type DatabaseClipboardInsert = Parameters<SnapshotBlockInserter>[0] & {
  source: DatabaseClipboardSource;
};

/**
 * Steps of a paste that moves a cut database. The cut left the database in
 * place; only the paste takes it out of its document. The target takes the
 * database before its source loses it: whatever is interrupted in between
 * leaves the database on both pages, never on none.
 */
export interface DatabaseMoveSteps {
  operationId: string;
  signal: AbortSignal;
  cancel(): void;
  /** Inserts the database at the paste target. Within one document it also
   * leaves its old place, in the same update. */
  insert(): void;
  /** Removes the database, and the rest of the blocks cut with it, from the
   * document it was cut from. Only after `insert`, and only once. */
  detach(): void;
}

export interface DatabaseClipboardPort {
  /** Materializes a new identity; resolves the insertion that places its
   * host block through `hold`, after the pasted blocks, and publishes the
   * copy with that page in one send. */
  copy(
    input: DatabaseClipboardInsert
  ): Promise<(hold: HistoryStepHold) => Promise<void>>;
  /** Warns before a move and lets the canonical document-sync owner rebind. */
  move(
    input: DatabaseClipboardInsert,
    steps: DatabaseMoveSteps
  ): Promise<void | 'deferred'>;
}

export const DatabaseClipboardProvider =
  createIdentifier<DatabaseClipboardPort>('DatabaseClipboard');

export function DatabaseClipboardExtension(
  port: DatabaseClipboardPort
): ExtensionType {
  return { setup: di => di.addImpl(DatabaseClipboardProvider, () => port) };
}

type Cut = {
  store: Store;
  expires: number;
  ready: boolean;
  /** Databases that wait for their paste, still at their place. */
  blocks: Map<string, { databaseId: string; rootDocId: unknown }>;
  /** Blocks the cut kept because they hold a database. The paste copied the
   * rest of them, so they leave with their last database. */
  containers: Set<string>;
  moved: Map<string, DatabaseClipboardSource>;
  active: Set<string>;
  abortController: AbortController;
  operations: Map<string, string>;
  /** Text of a selection across the database, removed with the paste. */
  text?: { from: TextAnchor; to: TextAnchor };
};

/** A text range held by Yjs positions: edits elsewhere in the document,
 * also in the same paragraph, never shift it. */
type TextAnchor = {
  blockId: string;
  start: Y.RelativePosition;
  end: Y.RelativePosition;
};

/** Text typed right before the range or right after it stays outside. */
function anchorText(
  blockId: string,
  text: Text,
  start: number,
  end: number
): TextAnchor {
  return {
    blockId,
    start: Y.createRelativePositionFromTypeIndex(text.yText, start, 0),
    end: Y.createRelativePositionFromTypeIndex(text.yText, end, -1),
  };
}

function resolveText(store: Store, anchor: TextAnchor) {
  const text = store.getModelById(anchor.blockId)?.text;
  const doc = text?.yText.doc;
  if (!text || !doc) return;
  const start = Y.createAbsolutePositionFromRelativePosition(anchor.start, doc);
  const end = Y.createAbsolutePositionFromRelativePosition(anchor.end, doc);
  if (start?.type !== text.yText || end?.type !== text.yText) return;
  return {
    text,
    index: start.index,
    length: Math.max(0, end.index - start.index),
  };
}
const cuts = new Map<string, Cut>();
const cutSlices = new WeakMap<Slice, string>();

type DatabaseTree = { flavour: string; children: readonly DatabaseTree[] };

/** Whether a model is a database or holds one. A cut leaves such a model in
 * place: the database leaves its document only with its paste. */
export function containsDatabaseBlock(model: DatabaseTree): boolean {
  return (
    model.flavour === 'affine:database' ||
    model.children.some(child => containsDatabaseBlock(child))
  );
}

const pendingCutKey = (docId: string, blockId: string) =>
  `${docId}\u0000${blockId}`;

/**
 * Databases of completed cuts that wait for their paste. Local UI state of
 * this editor only: it never enters a document, other members never see it,
 * and a reload ends it.
 */
const pendingCuts$ = signal<ReadonlySet<string>>(new Set());

/** Whether this editor shows the database as cut, waiting for its paste. */
export function isDatabaseCutPending(docId: string, blockId: string): boolean {
  return pendingCuts$.value.has(pendingCutKey(docId, blockId));
}

function endCutOnEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') endDatabaseCuts();
}

function syncPendingCuts() {
  const pending = new Set<string>();
  for (const cut of cuts.values())
    if (cut.ready && cut.expires >= Date.now())
      for (const blockId of cut.blocks.keys())
        pending.add(pendingCutKey(cut.store.id, blockId));
  pendingCuts$.value = pending;
  if (typeof document === 'undefined') return;
  if (pending.size > 0)
    document.addEventListener('keydown', endCutOnEscape, true);
  else document.removeEventListener('keydown', endCutOnEscape, true);
}

/** Ends every waiting cut except `keep`: new clipboard content or Escape.
 * The databases stay where they are and show normally again. */
function cancelCut(token: string) {
  const cut = cuts.get(token);
  cuts.delete(token);
  cut?.abortController.abort();
}

export function endDatabaseCuts(keep?: string) {
  for (const token of [...cuts.keys()]) if (token !== keep) cancelCut(token);
  syncPendingCuts();
}

export type DatabaseCut = ReturnType<typeof beginDatabaseCut>;

/** A pasted marker alone never authorizes a move. Only the same process's
 * successfully completed cut can retain an identity, once per database. */
export function beginDatabaseCut(store: Store, models: readonly DraftModel[]) {
  for (const [token, cut] of cuts)
    if (cut.expires < Date.now()) cancelCut(token);
  const token = crypto.randomUUID();
  const blocks: Cut['blocks'] = new Map();
  const walk = (model: DraftModel) => {
    if (model.flavour === 'affine:database') {
      const props = model.props as Record<string, unknown>;
      blocks.set(model.id, {
        databaseId:
          typeof props.databaseId === 'string' ? props.databaseId : model.id,
        rootDocId: props.databaseRootDocId,
      });
    }
    model.children.forEach(walk);
  };
  models.forEach(walk);
  const cut: Cut = {
    store,
    blocks,
    containers: new Set(),
    moved: new Map(),
    expires: Date.now() + CUT_LIFETIME_MS,
    ready: false,
    active: new Set(),
    abortController: new AbortController(),
    operations: new Map(),
  };
  cuts.set(token, cut);
  return {
    token,
    markSlice: (slice: Slice) => cutSlices.set(slice, token),
    /** The cut kept these blocks because they hold a database: they leave
     * their document together with it. */
    removeOnPaste: (models: readonly { id: string; flavour: string }[]) => {
      for (const model of models)
        if (model.flavour !== 'affine:database') cut.containers.add(model.id);
    },
    /**
     * A text selection across the database: its text leaves the document
     * with the paste, first and last paragraph joined as in a text cut.
     * `models` are the selected blocks in document order; the ones between
     * both ends leave with the paste, a database inside them only with its
     * own.
     */
    removeTextOnPaste: (
      selection: {
        from: { blockId: string; index: number; length: number };
        to: { blockId: string; length: number } | null;
      },
      models: readonly { id: string; flavour: string }[]
    ) => {
      const { from, to } = selection;
      const fromText = store.getModelById(from.blockId)?.text;
      const toText = to && store.getModelById(to.blockId)?.text;
      if (!to || !fromText || !toText) return;
      cut.text = {
        from: anchorText(
          from.blockId,
          fromText,
          from.index,
          from.index + from.length
        ),
        to: anchorText(to.blockId, toText, 0, to.length),
      };
      for (const model of models)
        if (
          model.id !== from.blockId &&
          model.id !== to.blockId &&
          model.flavour !== 'affine:database' &&
          !insideDatabase(store, model.id)
        )
          cut.containers.add(model.id);
    },
    complete: () => {
      cut.ready = true;
      syncPendingCuts();
      setTimeout(() => {
        if (cuts.get(token) !== cut) return;
        cancelCut(token);
        syncPendingCuts();
      }, CUT_LIFETIME_MS);
    },
    abort: () => {
      cancelCut(token);
      syncPendingCuts();
    },
  };
}

export function databaseCutMoveSignal(
  operationId: string
): AbortSignal | undefined {
  for (const cut of cuts.values())
    if ([...cut.operations.values()].includes(operationId))
      return cut.abortController.signal;
  return undefined;
}

// Receipts match the exact paste, so they cannot end a newer cut.
export function completeDatabaseCutMove(
  operationId: string,
  moved?: DatabaseClipboardSource
) {
  for (const cut of cuts.values()) {
    for (const [blockId, pendingId] of cut.operations) {
      if (pendingId !== operationId) continue;
      if (moved) {
        cut.moved.set(blockId, moved);
        removePastedRest(cut);
      }
      cut.operations.delete(blockId);
      cut.blocks.delete(blockId);
      cut.active.delete(blockId);
    }
  }
  syncPendingCuts();
}

export function stampDatabaseClipboardSnapshot(
  snapshot: BlockSnapshot,
  store: Store,
  cutToken?: string
): void {
  if (snapshot.flavour === 'affine:database') {
    snapshot.props[META] = {
      workspaceId: store.workspace.id,
      docId: store.id,
      blockId: snapshot.id,
      databaseId:
        typeof snapshot.props.databaseId === 'string'
          ? snapshot.props.databaseId
          : snapshot.id,
      cutToken,
    } satisfies DatabaseClipboardSource;
  }
  snapshot.children.forEach(child =>
    stampDatabaseClipboardSnapshot(child, store, cutToken)
  );
}

function readSource(snapshot: BlockSnapshot): DatabaseClipboardSource {
  const value = snapshot.props[META] as
    | Partial<DatabaseClipboardSource>
    | undefined;
  if (
    !value ||
    [value.workspaceId, value.docId, value.databaseId, value.blockId].some(
      id => typeof id !== 'string' || !id
    )
  ) {
    throw new Error(
      'Database clipboard source is missing. Copy the database again.'
    );
  }
  return value as DatabaseClipboardSource;
}

/** Called by replaceIdMiddleware so connectors and other references see the
 * same ID as the asynchronous database inserter. Validation happens in the
 * awaited insertion port, never by throwing in an RxJS observer. */
export function databaseClipboardBlockId(
  std: BlockStdScope,
  snapshot: BlockSnapshot
): string | undefined {
  if (snapshot.flavour !== 'affine:database') return;
  try {
    const source = readSource(snapshot);
    if (pendingCut(source, std.store)) return source.blockId;
  } catch {
    // The awaited importer reports the malformed clipboard and inserts nothing.
  }
  return crypto.randomUUID();
}

function pendingCut(
  source: DatabaseClipboardSource,
  target: Store
): Cut | undefined {
  const cut = source.cutToken ? cuts.get(source.cutToken) : undefined;
  const block = cut?.blocks.get(source.blockId);
  if (
    !cut ||
    !cut.ready ||
    cut.expires < Date.now() ||
    !block ||
    cut.active.has(source.blockId) ||
    block.databaseId !== source.databaseId ||
    cut.store.workspace.id !== source.workspaceId ||
    target.workspace.id !== source.workspaceId ||
    cut.store.id !== source.docId ||
    !cut.store.hasBlock(source.blockId)
  )
    return;
  return cut;
}

function insideDatabase(store: Store, id: string): boolean {
  for (
    let parent = store.getParent(id);
    parent;
    parent = store.getParent(parent)
  )
    if (parent.flavour === 'affine:database') return true;
  return false;
}

/** What the paste of a cut takes out of its document besides the database:
 * the cut text, then the blocks that hold no database any more. */
function removePastedRest(cut: Cut) {
  removeCutText(cut);
  removeEmptiedContainers(cut);
}

/** Removes the cut text once, as a text cut would have: both ranges, then
 * the rest of the last paragraph joins the first. A paragraph deleted in the
 * meantime keeps the other one apart. */
function removeCutText(cut: Cut) {
  const anchors = cut.text;
  if (!anchors) return;
  cut.text = undefined;
  const from = resolveText(cut.store, anchors.from);
  const to = resolveText(cut.store, anchors.to);
  for (const range of [from, to])
    if (range && range.length > 0) range.text.delete(range.index, range.length);
  const fromModel = cut.store.getModelById(anchors.from.blockId);
  if (!from || !to || !fromModel) return;
  from.text.join(to.text);
  cut.store.deleteBlock(anchors.to.blockId, { bringChildrenTo: fromModel });
}

/** Removes the blocks cut together with a database once they hold none of
 * its databases any more: the paste already copied the rest of them. */
function removeEmptiedContainers(cut: Cut) {
  for (const id of cut.containers) {
    const model = cut.store.getModelById(id);
    if (model && containsDatabaseBlock(model)) continue;
    cut.containers.delete(id);
    if (model) cut.store.deleteBlock(id);
  }
}

export function databaseClipboardExportMiddleware(
  std: BlockStdScope
): TransformerMiddleware {
  return ({ slots }) => {
    let token: string | undefined;
    const before = slots.beforeExport.subscribe(payload => {
      if (payload.type !== 'slice') return;
      token = cutSlices.get(payload.slice);
      // New clipboard content ends every earlier cut that waits for a paste.
      endDatabaseCuts(token);
    });
    const after = slots.afterExport.subscribe(payload => {
      if (payload.type === 'block')
        stampDatabaseClipboardSnapshot(payload.snapshot, std.store, token);
    });
    return () => {
      before.unsubscribe();
      after.unsubscribe();
    };
  };
}

export function databaseClipboardImportMiddleware(
  std: BlockStdScope
): TransformerMiddleware {
  return ({ transformerConfigs }) => {
    const insert: SnapshotBlockInserter = async input => {
      if (input.snapshot.flavour !== 'affine:database') return false;
      const source = readSource(input.snapshot);
      const port = std.provider.getOptional(DatabaseClipboardProvider);
      if (!port)
        throw new Error('Database clipboard is unavailable in this editor');
      if (source.workspaceId !== input.doc.workspace.id)
        throw new Error('Copy databases within the same workspace');
      await input.doc.pageStructure.loadMissingPartsOf([input.draft.id]);
      const cut = pendingCut(source, input.doc);
      const props = input.draft.props as Record<string, unknown>;
      delete props[META];
      if (cut) {
        if (input.draft.id !== source.blockId)
          throw new Error('Database cut changed during paste. Paste again.');
        // Reserved now: a second paste of the same cut is refused before
        // either inserts.
        cut.active.add(source.blockId);
        return {
          kind: 'after',
          cancel: () => cut.active.delete(source.blockId),
          insert: async hold => {
            let deferred = false;
            try {
              // Restore identity from the private cut record, never an arbitrary
              // root reference in clipboard JSON. Doc-sync owns the RBAC rebind.
              const placement = cut.blocks.get(source.blockId)!;
              props.databaseId = source.databaseId;
              props.databaseRootDocId = placement.rootDocId;
              let inserted = false;
              let detached = false;
              const add = (index: number | undefined) =>
                input.doc.addBlock(
                  'affine:database',
                  { ...props, id: input.draft.id },
                  input.parentId,
                  index
                );
              const operationId = crypto.randomUUID();
              cut.operations.set(source.blockId, operationId);
              const steps: DatabaseMoveSteps = {
                operationId,
                signal: cut.abortController.signal,
                cancel: () => {
                  if (source.cutToken) cancelCut(source.cutToken);
                  syncPendingCuts();
                },
                insert: () =>
                  hold(() => {
                    if (inserted) return;
                    inserted = true;
                    if (input.doc === cut.store) {
                      // One document: the database leaves and arrives in one update.
                      cut.store.transact(() => {
                        const parent = cut.store.getParent(source.blockId);
                        const from = parent?.children.findIndex(
                          child => child.id === source.blockId
                        );
                        cut.store.deleteBlock(source.blockId);
                        detached = true;
                        // Pasted behind its old place in the same parent: that
                        // index counted the database itself.
                        add(
                          parent?.id === input.parentId &&
                            from !== undefined &&
                            from >= 0 &&
                            input.index !== undefined &&
                            input.index > from
                            ? input.index - 1
                            : input.index
                        );
                        removePastedRest(cut);
                      });
                    } else add(input.index);
                    // A subsequent sync failure must not turn one cut into two hosts.
                    cut.operations.delete(source.blockId);
                    cut.blocks.delete(source.blockId);
                    cut.moved.set(source.blockId, {
                      workspaceId: source.workspaceId,
                      databaseId: source.databaseId,
                      docId: input.doc.id,
                      blockId: input.draft.id,
                    });
                    syncPendingCuts();
                  }),
                detach: () =>
                  hold(() => {
                    if (!inserted || detached) return;
                    detached = true;
                    cut.store.transact(() => {
                      cut.store.deleteBlock(source.blockId);
                      removePastedRest(cut);
                    });
                  }),
              };
              if (
                (await port.move({ ...input, source }, steps)) === 'deferred'
              ) {
                deferred = true;
                return 'deferred';
              }
            } finally {
              if (!deferred) {
                cut.operations.delete(source.blockId);
                cut.active.delete(source.blockId);
              }
            }
            return 'inserted';
          },
        };
      }
      if (input.draft.id === source.blockId)
        throw new Error('Database cut is already being pasted. Paste again.');
      const moved = source.cutToken
        ? cuts.get(source.cutToken)?.moved.get(source.blockId)
        : undefined;
      const copy = await port.copy({ ...input, source: moved ?? source });
      return {
        kind: 'after',
        cancel: () => {},
        insert: async hold => {
          await copy(hold);
          return 'inserted';
        },
      };
    };
    transformerConfigs.set(SNAPSHOT_BLOCK_INSERTER, insert);
    return () => {
      if (transformerConfigs.get(SNAPSHOT_BLOCK_INSERTER) === insert)
        transformerConfigs.delete(SNAPSHOT_BLOCK_INSERTER);
    };
  };
}
